'use client';

import {
  ArrowLeft,
  BriefcaseBusiness,
  Facebook,
  Globe,
  GraduationCap,
  Instagram,
  Linkedin,
  Twitter
} from 'lucide-react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React from 'react';

import pfpFallback from '@ui/assets/images/pfp-fallback.png';
import StarFilled from '@ui/components/icons/StarFilled';

import { ProfessionalShareButton } from './ProfessionalShareButton';

export interface ProfessionalTranslations {
  aboutInstructor: string;
  instructorSince: string;
  bookings: string;
  about: string;
  activities: string;
  reviews: string;
  noBioYet: string;
  noActivitiesFound: string;
  noReviewsYet: string;
  socials: string;
  socialMedia: string;
}

export interface ProfessionalProfileProps {
  id: string;
  name: string;
  profileImageUrl: string | null;
  label: string;
  joinedYear: number;
  bookingsCount: number;
  ratingAverage?: number;
  bio?: string;
  socials?: {
    instagram?: string;
    facebook?: string;
    twitter?: string;
    website?: string;
    linkedin?: string;
  };
  activitiesContent?: React.ReactNode;
  reviewsContent?: React.ReactNode;
  showReviews?: boolean;
  translations: ProfessionalTranslations;
  className?: string;
}

export function DesktopProfessionalProfile({
  id,
  name,
  profileImageUrl,
  label,
  joinedYear,
  bookingsCount,
  ratingAverage,
  bio,
  socials,
  activitiesContent,
  reviewsContent,
  showReviews = true,
  translations: t,
  className = ''
}: ProfessionalProfileProps) {
  const router = useRouter();

  const displaySocials = socials;
  const hasSocialLinks = displaySocials && Object.values(displaySocials).some((value) => !!value);

  return (
    <section className={`w-full relative ${className}`}>
      {/* Back button — absolute, matches activity detail page positioning */}
      <div className="absolute top-[30px] z-20 md:left-14 lg:left-24 xl:left-[calc((100vw-75rem)/2+1rem)]">
        <button
          type="button"
          onClick={() => router.back()}
          aria-label="Go back"
          className="flex items-center justify-center size-10 rounded-full bg-white text-neutral-900 shadow-md cursor-pointer hover:bg-neutral-50 hover:shadow-lg transition-all"
        >
          <ArrowLeft className="size-5" />
        </button>
      </div>

      <div className="layout-bounds pt-5 pb-4">
        {/* 2-column layout */}
        <div className="flex flex-row gap-12 items-start">
          {/* Left column: profile card (with bio inside) + socials */}
          <div className="w-2/5 flex-shrink-0 flex flex-col gap-8">
            {/* Profile card */}
            <div className="relative flex flex-col items-center text-center gap-3 p-6 rounded-2xl border border-neutral-100 bg-white shadow-sm">
              {/* Share button — top-right corner of the card */}
              <div className="absolute top-4 right-4 z-10">
                <ProfessionalShareButton professionalId={id} professionalName={name} iconOnly />
              </div>

              <div className="w-24 h-24 relative rounded-full overflow-hidden border border-gray-200 flex-shrink-0">
                {profileImageUrl ? (
                  <Image src={profileImageUrl} alt={name} fill className="object-cover" priority sizes="96px" />
                ) : (
                  <Image src={pfpFallback} alt={name} fill className="object-cover" sizes="96px" />
                )}
              </div>

              <div className="flex flex-col items-center gap-1">
                <h1 className="text-xl font-bold text-neutral-900">{name}</h1>
                <p className="text-neutral-500 text-sm">{label}</p>
              </div>

              <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1.5 text-xs text-neutral-600 mt-1">
                <div className="flex items-center gap-1.5">
                  <GraduationCap className="w-3.5 h-3.5" />
                  <span>
                    {t.instructorSince} {joinedYear}
                  </span>
                </div>
                <span className="text-neutral-300">•</span>
                <div className="flex items-center gap-1.5">
                  <BriefcaseBusiness className="w-3.5 h-3.5" />
                  <span>
                    {bookingsCount} {t.bookings}
                  </span>
                </div>
                {ratingAverage != null && ratingAverage > 0 && (
                  <>
                    <span className="text-neutral-300">•</span>
                    <div className="flex items-center gap-1">
                      <span>{ratingAverage.toFixed(1)}</span>
                      <StarFilled className="w-3 h-3 text-[#FDC812]" />
                    </div>
                  </>
                )}
              </div>

              {/* Bio — inside the card */}
              <div className="w-full mt-2 pt-4 border-t border-neutral-100 text-left">
                {bio ? (
                  <p className="text-sm text-neutral-800 leading-relaxed whitespace-pre-line">{bio}</p>
                ) : (
                  <p className="text-sm text-neutral-400 italic">{t.noBioYet}</p>
                )}
              </div>
            </div>

            {/* Social media links */}
            {hasSocialLinks && (
              <div className="flex flex-col gap-3">
                <h2 className="text-base font-semibold text-neutral-900">{t.socials}</h2>
                <div className="flex flex-col gap-3">
                  {displaySocials.website && (
                    <a
                      href={
                        displaySocials.website.startsWith('http')
                          ? displaySocials.website
                          : `https://${displaySocials.website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 text-primary-500 hover:text-primary-600 transition-colors"
                    >
                      <div className="p-2 rounded-full bg-primary-50">
                        <Globe size={16} />
                      </div>
                      <span className="text-sm text-neutral-600">
                        {displaySocials.website.replace(/(https?:\/\/)?(www\.)?/i, '')}
                      </span>
                    </a>
                  )}
                  {displaySocials.instagram && (
                    <a
                      href={`https://instagram.com/${displaySocials.instagram.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 text-primary-500 hover:text-primary-600 transition-colors"
                    >
                      <div className="p-2 rounded-full bg-primary-50">
                        <Instagram size={16} />
                      </div>
                      <span className="text-sm text-neutral-600">Instagram</span>
                    </a>
                  )}
                  {displaySocials.facebook && (
                    <a
                      href={`https://facebook.com/${displaySocials.facebook}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 text-primary-500 hover:text-primary-600 transition-colors"
                    >
                      <div className="p-2 rounded-full bg-primary-50">
                        <Facebook size={16} />
                      </div>
                      <span className="text-sm text-neutral-600">Facebook</span>
                    </a>
                  )}
                  {displaySocials.twitter && (
                    <a
                      href={`https://twitter.com/${displaySocials.twitter.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 text-primary-500 hover:text-primary-600 transition-colors"
                    >
                      <div className="p-2 rounded-full bg-primary-50">
                        <Twitter size={16} />
                      </div>
                      <span className="text-sm text-neutral-600">Twitter</span>
                    </a>
                  )}
                  {displaySocials.linkedin && (
                    <a
                      href={`https://linkedin.com/in/${displaySocials.linkedin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 text-primary-500 hover:text-primary-600 transition-colors"
                    >
                      <div className="p-2 rounded-full bg-primary-50">
                        <Linkedin size={16} />
                      </div>
                      <span className="text-sm text-neutral-600">LinkedIn</span>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right column: activities + reviews */}
          <div className="w-3/5 min-w-0 flex flex-col gap-10">
            {/* Activities — no heading so first card aligns with profile card top */}
            <div>
              {activitiesContent ? (
                activitiesContent
              ) : (
                <p className="text-sm text-neutral-400 italic">{t.noActivitiesFound}</p>
              )}
            </div>

            {/* Reviews */}
            {showReviews && (
              <div className="flex flex-col gap-4">
                <h2 className="text-lg font-semibold text-neutral-900">{t.reviews}</h2>
                {reviewsContent ? reviewsContent : <p className="text-sm text-neutral-400 italic">{t.noReviewsYet}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
